import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, X, Clock } from 'lucide-react';
import { useAsync } from '../lib/useAsync';
import { store } from '../lib/store';
import { IconChip, Chips, EmptyState, SkeletonList } from '../components/ui';
import { DOC_SIGLA, euro } from '../lib/format';
import type { DocumentDTO, WarrantyDTO } from '@shared/types';

type Filter = 'tutti' | 'documenti' | 'garanzie' | 'scadenza';
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'tutti', label: 'Tutti' },
  { value: 'documenti', label: 'Documenti' },
  { value: 'garanzie', label: 'Garanzie' },
  { value: 'scadenza', label: 'In scadenza' },
];

const RECENT_KEY = 'archivio.recent-searches';

interface Result {
  id: string;
  kind: 'document' | 'warranty';
  title: string;
  sub: string;
  sigla: string;
  expiring: boolean;
}

export function RicercaScreen() {
  const navigate = useNavigate();
  const docs = useAsync(() => store.documents(), []);
  const warr = useAsync(() => store.warranties(), []);

  const [raw, setRaw] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('tutti');
  const [recent, setRecent] = useState<string[]>(readRecent);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  // Debounce ~200ms (README §6.14).
  useEffect(() => {
    const t = setTimeout(() => setQuery(raw.trim()), 200);
    return () => clearTimeout(t);
  }, [raw]);

  const loading = docs.loading || warr.loading;

  const results = useMemo<Result[]>(() => {
    const q = query.toLowerCase();
    if (!q) return [];
    const out: Result[] = [];
    if (filter === 'tutti' || filter === 'documenti' || filter === 'scadenza') {
      for (const d of docs.data ?? []) {
        if (filter === 'scadenza') continue; // i documenti non hanno stato "in scadenza" qui
        if (matchesDoc(d, q)) {
          out.push({
            id: d.id, kind: 'document', title: d.title, sub: d.holder,
            sigla: DOC_SIGLA[d.type], expiring: false,
          });
        }
      }
    }
    if (filter === 'tutti' || filter === 'garanzie' || filter === 'scadenza') {
      for (const w of warr.data?.items ?? []) {
        if (filter === 'scadenza' && w.status !== 'expiring') continue;
        if (matchesWarr(w, q)) {
          out.push({
            id: w.id, kind: 'warranty', title: w.product,
            sub: `${w.seller ?? ''} · ${euro(w.amount_cents)}`,
            sigla: 'GAR', expiring: w.status === 'expiring',
          });
        }
      }
    }
    return out;
  }, [query, filter, docs.data, warr.data]);

  const commitRecent = (term: string) => {
    const t = term.trim();
    if (!t) return;
    const next = [t, ...recent.filter((r) => r !== t)].slice(0, 6);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignora */ }
  };

  const removeRecent = (term: string) => {
    const next = recent.filter((r) => r !== term);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* ignora */ }
  };

  const open = (r: Result) => {
    commitRecent(query);
    navigate(r.kind === 'document' ? `/documenti/${r.id}` : `/garanzie/${r.id}`);
  };

  return (
    <div className="screen">
      <div className="topbar">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Indietro">
          <ChevronLeft size={20} />
        </button>
        <div className="searchbar searchbar--active" style={{ flex: 1, margin: 0 }}>
          <Search size={18} style={{ color: 'var(--accent)' }} />
          <input
            ref={inputRef}
            className="searchbar__input"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="Cerca un documento…"
            aria-label="Cerca"
          />
          {raw && (
            <button className="searchbar__clear" onClick={() => setRaw('')} aria-label="Cancella">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <Chips options={FILTERS} active={filter} onChange={setFilter} />
      </div>

      {!query ? (
        recent.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Ricerche recenti</div>
            <div className="list">
              {recent.map((r) => (
                <div key={r} className="row" style={{ cursor: 'default' }}>
                  <span className="iconchip iconchip--sm"><Clock size={16} /></span>
                  <button className="row__body" style={{ textAlign: 'left', background: 'none', border: 'none', color: 'inherit' }} onClick={() => setRaw(r)}>
                    <div className="row__title" style={{ fontSize: 14 }}>{r}</div>
                  </button>
                  <button className="kv__copy" onClick={() => removeRecent(r)} aria-label={`Rimuovi ${r}`}>
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      ) : loading ? (
        <div style={{ marginTop: 20 }}><SkeletonList rows={3} /></div>
      ) : results.length === 0 ? (
        <div style={{ marginTop: 28 }}>
          <EmptyState message={`Nessun risultato per “${query}”.`} />
        </div>
      ) : (
        <div style={{ marginTop: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            {results.length} risultat{results.length === 1 ? 'o' : 'i'}
          </div>
          <div className="list">
            {results.map((r) => (
              <button key={r.kind + r.id} type="button" className="row" onClick={() => open(r)}>
                <IconChip>{r.sigla}</IconChip>
                <div className="row__body">
                  <div className="row__title">{highlight(r.title, query)}</div>
                  <div className="row__meta">{highlight(r.sub, query)}</div>
                </div>
                {r.expiring && <span className="badge badge--accent">scad.</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function matchesDoc(d: DocumentDTO, q: string): boolean {
  return (
    d.title.toLowerCase().includes(q) ||
    d.holder.toLowerCase().includes(q) ||
    (d.number ?? '').toLowerCase().includes(q)
  );
}
function matchesWarr(w: WarrantyDTO, q: string): boolean {
  return w.product.toLowerCase().includes(q) || (w.seller ?? '').toLowerCase().includes(q);
}

/** Evidenzia la porzione di testo che combacia con la query (§6.14). */
function highlight(text: string, q: string) {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="hl">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}
